import {
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fuzzyFilter } from "./utils/modelUtils";
import { getLeafCols } from "./utils/utils";

export const useTable = ({ defaultColumns, data, ...props}) => {
  const [columns,setColumns] = useState(() => [...defaultColumns]);
  const calculatedDefaultColumnOrder = useMemo(()=>{
    const colsOrd = []
    defaultColumns?.map(c=>getLeafCols(colsOrd,c))
    return colsOrd
  },[defaultColumns])
  useEffect(()=>{
    const colsOrd = []
    defaultColumns?.map(c=>getLeafCols(colsOrd,c))
    if(!defaultProfile?.columnOrder){
      setColumnOrder(colsOrd)
    }
    setColumns(defaultColumns)
  },[defaultColumns])
  const defaultProfile = useMemo(()=>{
    if(typeof props.defaultProfile === 'string'){
      return props.defaultProfile ? props.profiles?.find(p=>p.id === props.defaultProfile)?.profile : undefined
    }
    return props.defaultProfile
  },[props.defaultProfile, props.profiles])
  const [columnOrder, setColumnOrder] = useState(defaultProfile?.columnOrder ?? calculatedDefaultColumnOrder);
  useEffect(()=>{
    setColumnOrder( defaultProfile?.columnOrder ?? calculatedDefaultColumnOrder)
  },[calculatedDefaultColumnOrder, defaultProfile])

  const [columnVisibility, setColumnVisibility] = useState(defaultProfile?.columnVisibility ?? {});
  const [grouping, setGrouping] = useState(defaultProfile?.grouping ?? []);
  const [isSplit, setIsSplit] = useState(defaultProfile?.isSplit ?? true);
  const [rowSelection, setRowSelection] = useState({});
  const [columnPinning, setColumnPinning] = useState(defaultProfile?.columnPinning ?? {});
  const [columnFilters, setColumnFilters] = useState(defaultProfile?.columnFilters ?? []);
  const [globalFilter, setGlobalFilter] = useState(defaultProfile?.globalFilter ?? "");
  const [columnSizing,setColumnSizing] = useState(defaultProfile?.columnSizing ?? {});
  const [sorting, setSorting] = useState(defaultProfile?.sorting ?? [])
  const [expanded, setExpanded] = useState({})

  const [currentProfile,setCurrentProfile] = useState()


  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    autoResetPageIndex: true,
    autoResetExpanded: false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onColumnVisibilityChange: setColumnVisibility,
    onGroupingChange: setGrouping,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    state: {
      grouping,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnPinning,
      rowSelection,
      columnOrder,
      columnSizing,
      sorting,
      expanded,
    }, 
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  useEffect(()=>{
    if(!props.currentProfile || !props.profiles?.length) return
    const newCurrentProfile = props?.profiles?.find(p=>p.id === props.currentProfile)?.profile
    if(JSON.stringify(newCurrentProfile) !== JSON.stringify(currentProfile)){
      setColumnOrder(newCurrentProfile?.columnOrder ?? calculatedDefaultColumnOrder)
      setColumnVisibility(newCurrentProfile?.columnVisibility ?? {})
      setColumnFilters(newCurrentProfile?.columnFilters ?? [])
      setGrouping(newCurrentProfile?.grouping ?? [])
      setIsSplit(newCurrentProfile?.isSplit ?? true)
      setColumnPinning(newCurrentProfile?.columnPinning ?? {})
      setGlobalFilter(newCurrentProfile?.globalFilter ?? "")
      setColumnSizing(newCurrentProfile?.columnSizing ?? {})
      setSorting(newCurrentProfile?.sorting ?? [])
      setExpanded({})
    }
  },[currentProfile, props.profiles])

  useEffect(()=>{
    if(!props.currentProfile || !props.profiles?.length) return
    const currentProfile = props?.profiles?.find(p=>p.id === props.currentProfile)?.profile
    setColumnOrder(currentProfile?.columnOrder ?? calculatedDefaultColumnOrder)
    setColumnVisibility(currentProfile?.columnVisibility ?? {})
    setGrouping(currentProfile?.grouping ?? [])
    setIsSplit(currentProfile?.isSplit ?? true)
    setColumnPinning(currentProfile?.columnPinning ?? {})
    setColumnFilters(currentProfile?.columnFilters ?? [])
    setGlobalFilter(currentProfile?.globalFilter ?? "")
    setColumnSizing(currentProfile?.columnSizing ?? {})
    setSorting(currentProfile?.sorting ?? [])
    setCurrentProfile(currentProfile)
    setExpanded({})
  },[props.currentProfile,props.profiles])

  const exportProfile = useCallback(()=>{
    return {
      id: props.currentProfile,
      profile: {
        columnFilters,
        columnOrder,
        columnPinning,
        columnSizing,
        columnVisibility,
        globalFilter,
        grouping,
        isSplit,
        sorting
      }
    }
  },[columnFilters, columnOrder, columnPinning, columnSizing, columnVisibility, globalFilter, grouping, isSplit, props.currentProfile, sorting])

  return {
    table,
    columnOrder,
    setColumnOrder,
    columnVisibility,
    setColumnVisibility,
    grouping,
    setGrouping,
    isSplit,
    setIsSplit,
    rowSelection,
    setRowSelection,
    columnPinning,
    setColumnPinning,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
    columnSizing,
    setColumnSizing,
    expanded,
    setExpanded,
    exportProfile
  };
};
